import { MODULE_ROUTE } from './routes';
import type {
  SentryFlowBuildOptions,
  SentryFlowDefinition,
  SentryModule,
} from '../types';

const routeHintsFor = (
  module: SentryModule,
  override?: readonly string[],
): readonly string[] => override ?? [MODULE_ROUTE[module]];

const buildFlow = (
  module: SentryModule,
  feature: string,
  event: string | readonly string[] = [],
  options?: SentryFlowBuildOptions,
): SentryFlowDefinition => ({
  module,
  feature,
  event,
  eventPattern: options?.eventPattern,
  notes: options?.notes,
  routeHints: routeHintsFor(module, options?.routes),
});

export const TABLE_FLOW_EVENTS = {
  table_filters: 'Table_Filters:Apply',
  date_filters: 'Table_Date_Filters:Apply',
  export: 'Primary_Button_Export',
  row_menu: 'Table_Row_EllipsisPopup',
  quick_create: 'Quick_Create_Button',
} as const;

type TableOp = keyof typeof TABLE_FLOW_EVENTS;

const tableFlows = (module: SentryModule, ops: readonly TableOp[]) =>
  ops.map((op) => buildFlow(module, op, TABLE_FLOW_EVENTS[op]));

export const P0_FLOWS: readonly SentryFlowDefinition[] = [
  buildFlow('landing', 'access_product', 'Landing_Page_Primary_Button_Access', {
    notes: 'PG landing entry',
  }),
  buildFlow(
    'summary',
    'dashboard_redirect',
    'Primary_Button_Dashboard_Redirect',
    {
      notes: 'Cross-product CTA',
    },
  ),
  buildFlow('onboarding', 'new_form', 'New_Onboarding_Form', {
    notes: 'Blocks new merchants',
  }),
  buildFlow(
    'payment_links',
    'create_link',
    ['Primary_Button_Create Payment Link', 'Create Payment Link'],
    { notes: 'Core PG revenue' },
  ),
  buildFlow('payment_links', 'create_and_send', 'Primary_Button_Create&Send'),
  buildFlow('payment_links', 'create', 'Primary_Button_Create'),
  buildFlow('payment_links', 'update', 'Primary_Button_Update'),
  buildFlow('payment_links', 'send', 'Primary_Button_Send'),
  buildFlow(
    'payment_forms',
    'create_payment_form',
    'Primary_Button_Create Payment Form',
  ),
  buildFlow('payment_forms', 'create', 'Primary_Button_Create'),
  buildFlow('payment_forms', 'save', 'Primary_Button_Save'),
  buildFlow(
    'payment_buttons',
    'create_button',
    'Primary_Button_CreatePaymentButton',
  ),
  buildFlow(
    'payments',
    'refund_modal_submit',
    'Primary_Button_Refund_Modal_Submit',
    {
      routes: ['/transactions/payments', '/transactions/orders'],
    },
  ),
  buildFlow('payments', 'refund_view', 'Primary_Button_Refund'),
  buildFlow('payments', 'initiate_refund_row', 'Table_Row_InitiateRefund'),
  buildFlow('settlements', 'withdraw_now', 'Primary_Button_WithdrawNow'),
  buildFlow('settlements', 'withdraw_confirm', 'Primary_Button_Confirm'),
  buildFlow(
    'settlements',
    'activate_instantly',
    'Primary_Button_ActivateInstantly',
  ),
  buildFlow('payouts', 'create_transfer', 'Primary_Button', {
    routes: ['/payout', '/transfers'],
  }),
  buildFlow('payouts', 'transfer_2fa', 'Confirm_2FA_Auth', {
    routes: ['/payout', '/transfers'],
  }),
  buildFlow('developers', 'api_key_generated', 'pg_api_key_generated', {
    routes: ['/developers', '/api-keys'],
  }),
  buildFlow('developers', 'api_key_2fa', 'Confirm_2FA_Auth', {
    routes: ['/developers/api-keys', '/api-keys'],
  }),
  buildFlow(
    'developers',
    'api_key_download',
    'Primary_Button_Download_API_Key',
    {
      routes: ['/developers', '/api-keys'],
    },
  ),
  buildFlow('developers', 'webhook_add', 'Primary_Button_AddWebhookEndpoint', {
    routes: ['/webhooks', '/developers'],
  }),
  buildFlow('developers', 'webhook_save', 'Primary_Button_Save', {
    routes: ['/webhooks', '/developers/webhooks'],
  }),
  buildFlow(
    'disputes',
    'take_action',
    ['Table_Row_Button_TakeAction', 'Primary_Button_TakeAction'],
    { routes: ['/disputes'] },
  ),
  buildFlow('disputes', 'submit_evidence', 'Primary_Button_SubmitEvidence', {
    routes: ['/disputes'],
  }),
  buildFlow('disputes', 'accept_dispute', 'Primary_Button_AcceptDispute', {
    routes: ['/disputes'],
  }),
  buildFlow(
    'landing',
    'terms_agree',
    'Primary_Button_Terms_And_Conditions_I_Agree',
    { routes: ['/landing', '/merchants'] },
  ),
  buildFlow('landing', 'verify_now', 'Primary_Button_Verify_Now', {
    routes: ['/landing', '/merchants'],
  }),
  buildFlow('landing', 'complete_now', 'Primary_Button_Complete_Now', {
    routes: ['/landing', '/merchants'],
  }),

  // --- First-batch gaps (auth, money movement, activation) ---
  buildFlow(
    'login',
    'login_email',
    'Login_Page_Email_Id_Primary_Button_Log_In',
    {
      // Empty hints: login emits OB:> (not a path); match by event only
      routes: [],
      notes: 'Auth entry — email login',
    },
  ),
  buildFlow(
    'login',
    'login_alias',
    'Login_Page_Account_Alias_Primary_Button_Log_In',
    { routes: [], notes: 'Auth entry — alias login' },
  ),
  buildFlow('login', 'login_google', 'Google login clicked', {
    routes: [],
    notes: 'Auth entry — Google OAuth',
  }),
  buildFlow('signup', 'signup_continue', 'Signup_Page_Primary_Button_Continue', {
    routes: [],
    notes: 'Signup step 1',
  }),
  buildFlow(
    'signup',
    'signup_create_account',
    'Signup_Page_Create_Your_Account_Primary_Button_Create_Account',
    { routes: [], notes: 'Account creation confirm' },
  ),
  buildFlow('signup', 'tnc_signup_accept', 'TnC Signup accept', {
    routes: [],
    notes: 'Blocks signup until accepted',
  }),
  buildFlow('auth', 'tnc_product_accept', 'TnC Product accept', {
    routes: [],
    notes: 'Blocks product use until accepted',
  }),
  buildFlow(
    'landing',
    'product_activation_confirm',
    'Primary_Button_ProductActivation_Confirm',
    {
      routes: ['/landing', '/merchants'],
      notes: 'Confirms product activation request',
    },
  ),
  buildFlow('landing', 'request_activation', 'Click Request Activation', {
    routes: ['/landing', '/merchants'],
    notes: 'Activation request click',
  }),
  buildFlow('payments', 'capture', 'Primary_Button_Capture', {
    routes: ['/transactions/payments', '/timeline'],
    notes: 'Pre-auth capture — money movement',
  }),
  buildFlow('payments', 'void', 'Form_Timeline_Button_Void', {
    routes: ['/transactions/payments', '/timeline'],
    notes: 'Void pre-auth',
  }),
  buildFlow(
    'payments',
    'instant_refund_activation',
    'Primary_Button_Confirm_Activation',
    {
      routes: ['/transactions/payments', '/transactions/refunds'],
      notes:
        'Instant refund product activation (≠ settlement ConfirmActivation)',
    },
  ),
  buildFlow(
    'payments',
    'refund_wallet_recharge',
    'Primary_Button_Confirm_Recharge',
    {
      routes: ['/transactions/payments', '/transactions/refunds'],
      notes: 'Wallet recharge to enable refund',
    },
  ),
  buildFlow('payments', 'batch_upload_file', 'Primary_Button_UploadFile', {
    routes: ['/transactions/payments'],
    notes: 'Batch capture / payment file upload',
  }),
  buildFlow('refunds', 'batch_upload_file', 'Primary_Button_UploadFile', {
    routes: ['/transactions/refunds', '/batch-refunds'],
    notes: 'Batch refund file upload',
  }),
  buildFlow(
    'settings',
    'enable_instant_refund',
    'Primary_Button_EnableInstantRefund',
    {
      routes: ['/settings', '/preferences'],
      notes: 'Enables instant refund capability',
    },
  ),
  buildFlow(
    'settlements',
    'confirm_activation',
    'Primary_Button_ConfirmActivation',
    {
      routes: ['/settlements'],
      notes: 'Instant settlement activation confirm',
    },
  ),
  buildFlow('settlements', 'activate_now', 'Primary_Button_ActivateNow', {
    routes: ['/settlements', '/on-demand'],
    notes: 'On-demand settlement activation',
  }),
  buildFlow(
    'settlements',
    'transfer_balance',
    'Primary_Button_TransferBalance',
    {
      routes: ['/settlements', '/on-demand'],
      notes: 'On-demand balance transfer',
    },
  ),
  buildFlow(
    'payment_links',
    'cancel_link',
    'Danger_Button_Cancel_Payment_Link_Confirm',
    {
      routes: ['/payment-links'],
      notes: 'Cancels live payment link',
    },
  ),
  buildFlow('payment_links', 'create_batch', 'Create Payment Batch', {
    routes: ['/payment-links'],
    notes: 'Batch payment-link create',
  }),
  buildFlow(
    'payment_links',
    'batch_proceed_anyway',
    'Primary_Button_ProceedAnyway',
    {
      routes: ['/payment-links'],
      notes: 'Proceed despite batch validation errors',
    },
  ),
  buildFlow(
    'payment_forms',
    'create_intelli_form',
    'Primary_Button_Create Intelli Form',
    { notes: 'Intelli form create (≠ Create Payment Form)' },
  ),
  buildFlow('easy_split', 'settle_now', 'Primary_Button_SettleNow', {
    routes: ['/easy-split'],
    notes: 'Vendor settle-now',
  }),
  buildFlow('easy_split', 'settle_to_vendor', 'Primary_Button_SettleToVendor', {
    routes: ['/easy-split'],
    notes: 'Settle to vendor',
  }),
  buildFlow('easy_split', 'add_vendor', 'Primary_Button_Add Vendor', {
    routes: ['/easy-split', '/vendors'],
    notes: 'Add vendor CTA',
  }),
  buildFlow(
    'easy_split',
    'kyc_submit_for_approval',
    'Primary_Button_Submit_For_Approval',
    { routes: ['/easy-split', '/vendors'], notes: 'Vendor KYC submit' },
  ),
  buildFlow('easy_split', 'kyc_confirm', 'Primary_Button_Submit_KYC_Confirm', {
    routes: ['/easy-split', '/vendors'],
    notes: 'Vendor KYC confirm modal',
  }),
  buildFlow(
    'subscriptions',
    'create_subscription',
    [
      'PG_Subscriptions_AllSubs_CreateSubscriptionButton',
      'PG_Subscriptions_CreateSubModal_Preview_CreateSubscriptionButton',
    ],
    {
      routes: ['/subscriptions'],
      notes: 'Create subscription funnel',
    },
  ),
  buildFlow(
    'subscriptions',
    'raise_payment',
    'PG_Subscriptions_SubDetails_RaisePaymentModalSubmit',
    {
      routes: ['/subscriptions'],
      notes: 'Raise / charge subscription payment',
    },
  ),
  buildFlow(
    'subscriptions',
    'refund',
    'PG_Subscriptions_SubDetails_RefundSubmit',
    { routes: ['/subscriptions'], notes: 'Subscription refund' },
  ),
  buildFlow(
    'subscriptions',
    'request_activation',
    'PG_Subscriptions_Landing_RequestActivationButton',
    {
      routes: ['/subscriptions'],
      notes: 'Subscriptions product activation',
    },
  ),
  buildFlow(
    'one_click_checkout',
    'request_activation',
    ['Request_checkout_one', 'merchant_checkout_one_request'],
    {
      routes: ['/one-click-checkout', '/checkout-one'],
      notes: 'Checkout One activation request',
    },
  ),
  buildFlow(
    'risk_shield',
    'start_free_trial_disputes',
    'Start_Free_Trial_Button_Riskshield_Modal_In_Disputes',
    {
      routes: ['/disputes', '/transactions/disputes', '/risk-shield'],
      notes: 'Risk Shield trial from disputes modal',
    },
  ),
  buildFlow(
    'risk_shield',
    'start_free_trial_summary',
    'Start_Free_Trial_RiskShield_Card_In_Summary',
    {
      routes: ['/summary', '/risk-shield'],
      notes: 'Risk Shield trial from summary card',
    },
  ),
  buildFlow(
    'developers',
    'generate_api_keys',
    'Primary_Button_Generate_API_Keys',
    {
      routes: ['/developers', '/api-keys'],
      notes: 'Generate API keys click (before 2FA)',
    },
  ),
];

export const P1_FLOWS: readonly SentryFlowDefinition[] = [
  ...tableFlows('payments', ['table_filters', 'date_filters', 'export']),
  ...tableFlows('payment_links', [
    'row_menu',
    'table_filters',
    'date_filters',
    'quick_create',
  ]),
  ...tableFlows('payment_forms', ['row_menu', 'table_filters', 'quick_create']),
  buildFlow('reports', 'generate_new', 'Primary_Button_GenerateNewReport'),
  buildFlow('reports', 'generate', 'Primary_Button_GenerateReport'),
  ...tableFlows('reports', ['row_menu', 'table_filters', 'date_filters']),
  ...tableFlows('orders', ['table_filters', 'date_filters', 'export']),
  ...tableFlows('settlements', ['table_filters', 'date_filters']),
  ...tableFlows('refunds', ['table_filters', 'date_filters']),
  ...tableFlows('disputes', ['table_filters', 'export']),
  ...tableFlows('easy_split', ['table_filters']),
  buildFlow(
    'developers',
    'webhook_logs_filters',
    TABLE_FLOW_EVENTS.table_filters,
    {
      routes: ['/developers/webhooks/logs'],
    },
  ),
  ...tableFlows('customers', ['date_filters']),
  buildFlow('home', 'quick_create', TABLE_FLOW_EVENTS.quick_create),
  buildFlow('home', 'product_tour', 'sage_tour_shown', {
    routes: [
      '/summary/overview',
      '/transactions/payments',
      '/payment-links',
      '/settlements',
    ],
  }),
  buildFlow('settings', 'enable_payment_method', [], {
    eventPattern: /^Primary_Button_Enable_.*_Confirm$/,
    routes: ['/settings/payment-methods'],
    notes: 'Revenue — payment method activation on settings page',
  }),
  buildFlow(
    'offers',
    'create_offer',
    [
      'Primary_Button_CreateNewOffer',
      'OffersV2_Primary_Button_CreateNewOffer',
      'Primary_Button_CreateNewOffer_Unleash',
      'OffersV2_Dropdown_CreateNewOffer',
    ],
    {
      routes: ['/offers'],
      notes: 'Revenue — merchant creates a new offer',
    },
  ),
  buildFlow('offers', 'create_offer_submit', 'Primary_Button_Create', {
    routes: ['/offers/all/offers-description', '/offers/all'],
    notes: 'Revenue — offer creation confirm step',
  }),
  buildFlow('risk_shield', 'enable_network_intelligence', [], {
    eventPattern: /Network[_\s-]*Intelligence/i,
    routes: ['/configurations', '/risk-shield'],
    notes:
      'Revenue/Risk — Cashfree Network Intelligence config enable (federated RS)',
  }),
  buildFlow('risk_shield', 'enable_goi_blacklist', [], {
    eventPattern: /Government[_\s-]*of[_\s-]*India.*Blacklist|GOI.*Blacklist/i,
    routes: ['/configurations', '/risk-shield'],
    notes:
      'Revenue/Risk — Government of India blacklist config enable (federated RS)',
  }),
  buildFlow(
    'risk_shield',
    'add_to_blacklist',
    'Add_To_Riskshield_Blacklist_Add_Now',
    {
      routes: ['/disputes/dispute-details', '/transactions/disputes'],
      notes: 'Risk Shield — add customer to blacklist from dispute',
    },
  ),
  buildFlow(
    'vba',
    'create_virtual_account',
    'Primary_Button_CreateVirtualAccount',
    {
      routes: ['/vba-configuration/accounts'],
      notes: 'Enterprise — VBA account creation (financial services cohort)',
    },
  ),

  // --- First-batch P1 gaps ---
  buildFlow(
    'subscriptions',
    'create_plan',
    [
      'PG_Subscriptions_Plans_CreatePlanButton',
      'PG_Subscriptions_CreatePlanModal_Preview_CreatePlanButton',
    ],
    { routes: ['/subscriptions'], notes: 'Subscription plan create' },
  ),
  buildFlow(
    'subscriptions',
    'batch_upload',
    [
      'PG_Subscriptions_BatchSub_UploadModalUploadButton',
      'PG_Subscriptions_BatchPayments_UploadModalUploadButton',
      'PG_Subscriptions_ImportMandates_UploadModalUploadButton',
    ],
    {
      routes: ['/subscriptions'],
      notes: 'Subscription batch / mandate upload',
    },
  ),
  buildFlow('refunds', 'cancel_refund', 'Button_CancelRefund', {
    routes: ['/transactions/refunds'],
    notes: 'Cancel in-flight refund',
  }),
  buildFlow(
    'refunds',
    'cancel_refund_detail',
    'RefundDetail_Button_CancelRefund',
    {
      routes: ['/transactions/refunds'],
      notes: 'Cancel refund from detail',
    },
  ),
  buildFlow('payments', 'force_recon', 'Primary_Button_ForceRecon', {
    routes: ['/transactions/payments', '/timeline'],
    notes: 'Force recon from timeline',
  }),
  buildFlow('payments', 'timeline_split', 'Primary_Button_Split', {
    routes: ['/transactions/payments', '/timeline'],
    notes: 'Manual split from payment timeline',
  }),
  buildFlow('reports', 'schedule_now', 'Primary_Button_ScheduleNow', {
    routes: ['/reports'],
    notes: 'Schedule report create',
  }),
];
